/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export const GTM_PRODUCT_ATTRIBUTES_KEY = 'gtmProductAttributes';
export const GTM_AUTH_INTENT_KEY = 'gtmAuthIntent';

export const LIST_PRODUCT_LISTING = 'product listing page';
export const LIST_SEARCH_RESULTS = 'search results';
export const LIST_RECERTIFIED = 'recertified product listing page';
export const LIST_CLEARANCE = 'clearance product listing page';
export const LIST_RELATED = 'related products';
export const LIST_RECOMMENDED = 'recommended products';
export const LIST_PRODUCT_IDENTIFIER = 'product identifier list';
export const LIST_RECOMMENDED_PARTS = 'recommended parts list';
export const LIST_RECENTLY_VIEWED = 'recently viewed';

const TRADEMARKS = /[®™©]/g;

export type GtmItem = {
    item_name: string;
    item_id: string;
    affiliation: 'deltafaucet.com';
    coupon: '';
    currency: string;
    discount: '';
    index: number;
    item_brand: string;
    item_category: string;
    item_category2: string;
    item_category3: string;
    item_category4: string;
    item_category5: string;
    item_list_name: string;
    item_variant?: string;
    price: number;
    quantity: number;
};

export type GtmProductSource = {
    id?: string;
    productId?: string;
    name?: string;
    productName?: string;
    price?: number;
    currency?: string;
    primaryCategoryId?: string;
    representedProduct?: GtmProductSource;
    [key: string]: unknown;
};

type StoredProductAttribute = {
    pid: string;
    index: number | null;
    list: string;
};

export type DataLayerPushOptions = {
    enabled: boolean;
    consentGranted: boolean;
};

declare global {
    interface Window {
        dataLayer?: Array<Record<string, unknown>>;
    }
}

function readString(product: GtmProductSource | undefined, keys: string[]): string | undefined {
    if (!product) return undefined;
    for (const key of keys) {
        const direct = product[key];
        if (typeof direct === 'string' && direct.trim()) return direct.trim();
    }
    const attrs = product.customAttributes;
    if (Array.isArray(attrs)) {
        for (const key of keys) {
            const match = attrs.find(
                (attr) =>
                    attr &&
                    typeof attr === 'object' &&
                    'id' in attr &&
                    (attr.id === key || attr.id === key.replace(/^c_/, ''))
            ) as { value?: unknown } | undefined;
            if (typeof match?.value === 'string' && match.value.trim()) return match.value.trim();
        }
    }
    if (product.representedProduct) return readString(product.representedProduct, keys);
    return undefined;
}

export function listNameForCategory(categoryId?: string): string {
    const id = (categoryId ?? '').toLowerCase();
    if (id.includes('recertified')) return LIST_RECERTIFIED;
    if (id.includes('clearance')) return LIST_CLEARANCE;
    return LIST_PRODUCT_LISTING;
}

/** Maps Einstein recommender ids used on the PDP to the legacy SFCC list names. */
export function listNameForRecommender(recommenderName?: string): string | undefined {
    switch (recommenderName) {
        case 'viewed-recently-einstein':
            return LIST_RECENTLY_VIEWED;
        case 'complete-the-set':
            return LIST_RELATED;
        case 'pdp-similar-items':
        case 'product-to-product-einstein':
            return LIST_RECOMMENDED;
        default:
            return undefined;
    }
}

export function contentGroupForPath(pathname: string): 'Product' | 'Category' | 'Search' | 'Other' {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (path.includes('/product/') || path.endsWith('/product')) return 'Product';
    if (path.includes('/category/') || path.endsWith('/category')) return 'Category';
    if (path.includes('/search')) return 'Search';
    return 'Other';
}

export function toVisitorId(shopperId?: string): string {
    if (!shopperId || typeof btoa !== 'function') return '';
    try {
        return btoa(shopperId);
    } catch {
        return '';
    }
}

export function toGtmItem(
    product: object,
    options: { currency: string; index?: number; listName: string; categoryId?: string }
): GtmItem {
    const source = product as GtmProductSource;
    const itemId = source.productId || source.id || '';
    const collection = readString(source, ['c_collection', 'collection']);
    const finish = readString(source, ['c_finish', 'finish']);
    const categoryId = options.categoryId || source.primaryCategoryId || readString(source, ['c_primaryCategoryId']);
    const categories = (categoryId ?? '').split('_').filter(Boolean);
    const price = typeof source.price === 'number' ? source.price : 0;

    const item: GtmItem = {
        item_name: source.productName || source.name || '',
        item_id: itemId,
        affiliation: 'deltafaucet.com',
        coupon: '',
        currency: source.currency || options.currency,
        discount: '',
        index: options.index ?? 1,
        item_brand: collection ? collection.replace(TRADEMARKS, '').toLowerCase() : 'delta',
        item_category: categories[0] ?? '',
        item_category2: categories[1] ?? '',
        item_category3: categories[2] ?? '',
        item_category4: categories[3] ?? '',
        item_category5: categories[4] ?? '',
        item_list_name: options.listName,
        price,
        quantity: 1,
    };
    if (finish) item.item_variant = finish;
    return item;
}

export function pushDataLayer(event: Record<string, unknown>, options: DataLayerPushOptions): void {
    if (typeof window === 'undefined' || !options.enabled || !options.consentGranted) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

function readStoredAttributes(): StoredProductAttribute[] {
    if (typeof sessionStorage === 'undefined') return [];
    try {
        const raw = sessionStorage.getItem(GTM_PRODUCT_ATTRIBUTES_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as StoredProductAttribute[];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function rememberProductList(pid: string, index: number | null, list: string): void {
    if (typeof sessionStorage === 'undefined' || !pid) return;
    const products = readStoredAttributes().filter((product) => product.pid !== pid);
    products.push({ pid, index, list });
    sessionStorage.setItem(GTM_PRODUCT_ATTRIBUTES_KEY, JSON.stringify(products));
}

/** Applies a stored list name to a view_item, then removes that product's entry. */
export function consumeStoredListName(pid: string, fallback: string): string {
    if (typeof sessionStorage === 'undefined' || !pid) return fallback;
    const products = readStoredAttributes();
    const match = products.find((product) => product.pid === pid);
    if (!match) return fallback;
    sessionStorage.setItem(
        GTM_PRODUCT_ATTRIBUTES_KEY,
        JSON.stringify(products.filter((product) => product.pid !== pid))
    );
    return match.list || fallback;
}

export function buildViewItemList(currency: string, listName: string, items: GtmItem[]): Record<string, unknown> {
    return {
        event: 'view_item_list',
        ecommerce: { currency, item_list_name: listName, items },
    };
}

export function buildSelectItem(currency: string, listName: string, item: GtmItem): Record<string, unknown> {
    return {
        event: 'select_item',
        ecommerce: { currency, item_list_name: listName, items: [item] },
    };
}

export function buildViewItem(currency: string, item: GtmItem): Record<string, unknown> {
    return {
        event: 'view_item',
        ecommerce: { currency, items: [item] },
    };
}

export function buildAddToWishlist(currency: string, item: GtmItem): Record<string, unknown> {
    return {
        event: 'add_to_wishlist',
        ecommerce: { currency, value: item.price, items: [item] },
    };
}

export function buildPageIdentity(input: {
    pathname: string;
    language: string;
    currency: string;
    shopperId?: string;
    customerId?: string;
    loggedIn: boolean;
}): Record<string, unknown> {
    return {
        content_group: contentGroupForPath(input.pathname),
        language: input.language,
        currency: input.currency,
        visitor_id: toVisitorId(input.shopperId),
        customer_id: input.loggedIn ? (input.customerId ?? '') : '',
        logged_in_status: input.loggedIn ? 'Logged In' : 'Not Logged In',
    };
}

export function buildNavigationClick(category: string, label: string): Record<string, unknown> | undefined {
    const text = label.trim().toLowerCase();
    if (!text) return undefined;
    return {
        event: 'navigationClick',
        category,
        action:
            category === 'utility navigation clicks'
                ? 'clicked utility navigation link'
                : 'clicked main menu navigation link',
        label: text,
    };
}

export function buildFooterClick(label: string): Record<string, unknown> | undefined {
    const text = label.trim().toLowerCase();
    if (!text) return undefined;
    return {
        event: 'customEvent',
        category: 'footer navigation clicks',
        action: 'clicked a footer link',
        label: text,
    };
}

export function buildSortClick(action: string, url: string): Record<string, unknown> | undefined {
    if (!action || !url) return undefined;
    return { event: 'customEvents', category: 'used sort filter', action, label: url };
}

export function buildFilterClick(action: string, url: string): Record<string, unknown> | undefined {
    if (!action || !url) return undefined;
    return { event: 'customEvents', category: 'used filter by filter', action, label: url };
}

export function buildSelectPromotion(creativeName: string, promotionName: string): Record<string, unknown> | undefined {
    const creative_name = creativeName.trim().toLowerCase();
    const promotion_name = promotionName.trim().toLowerCase();
    if (!creative_name || !promotion_name) return undefined;
    return { event: 'select_promotion', ecommerce: { creative_name, promotion_name } };
}

export function normalizeUtilityLabel(label: string): string {
    const text = label.toLowerCase();
    if (text.includes('search')) return 'search';
    if (text.includes('wishlist') || text.includes('favorite')) return 'favorites';
    if (text.includes('cart')) return 'cart';
    return label.trim().toLowerCase();
}

export type GtmAuthIntent = 'login' | 'signup';

export function rememberAuthIntent(intent: GtmAuthIntent): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(GTM_AUTH_INTENT_KEY, intent);
}

export function consumeAuthIntent(): GtmAuthIntent | undefined {
    if (typeof sessionStorage === 'undefined') return undefined;
    const value = sessionStorage.getItem(GTM_AUTH_INTENT_KEY);
    sessionStorage.removeItem(GTM_AUTH_INTENT_KEY);
    return value === 'login' || value === 'signup' ? value : undefined;
}

export function buildAuthEvent(input: {
    intent: GtmAuthIntent;
    shopperId?: string;
    customerId?: string;
    method?: string;
    loginCount?: number;
}): Record<string, unknown> {
    const method = !input.method || input.method === 'auth0' ? 'email' : input.method;
    return {
        event: input.intent === 'signup' ? 'sign_up' : 'login',
        method,
        visitor_id: toVisitorId(input.shopperId),
        customer_id: input.customerId ?? '',
        customer_type: input.intent === 'signup' ? 'New' : 'Registered',
        logged_in_status: 'Logged In',
        login_count: input.loginCount ?? 1,
    };
}
